"use client";

import { Button } from "@/components/atoms/Button";
import {
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { useRef } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

// API data structure (from CMS)
interface APICounselingData {
  id?: number;
  page_id?: number;
  component_id?: number;
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  cta_text?: string;
  cta_text_color?: string;
  cta_button_background_color?: string;
  cta_button_icon?: string;
  cta_button_icon_alt?: string;
  background_color?: string;
  variant?: string;
  status?: number;
  is_deleted?: number;
}

const GetFreeCounselling1 = ({ data }: any) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLElement>(null);

  // Handle the data structure - data comes directly from API via SECTION_VARIANTS
  const sectionData: APICounselingData = data?.data || data || {};

  const title = sectionData.title || "";
  const title_text_color = sectionData.title_text_color || "#000000";
  const sub_title = sectionData.sub_title || "";
  const sub_title_text_color = sectionData.sub_title_text_color || "#000000";
  const cta_text = sectionData.cta_text || "";
  const cta_text_color = sectionData.cta_text_color || "#FFFFFF";
  const cta_button_background_color =
    sectionData.cta_button_background_color || "#000000";
  const cta_button_icon = sectionData.cta_button_icon || "";
  const cta_button_icon_alt = sectionData.cta_button_icon_alt || "";
  const background_color = sectionData.background_color || "#FFFFFF";
  const status = sectionData.status ?? 1;

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "counseling_section");
  //   }
  // }, []);

  // Don't render if no data or status is disabled
  if (!sectionData || Object.keys(sectionData).length === 0 || status === 0) {
    return null;
  }

  const handleCtaClick = () => {
    ctaClick({
      section: "counseling_section",
      component: "counseling_cta",
      label: cta_text,
      step: "1",
      entity: "counseling",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      })
    );
  };

  return (
    <section
      ref={ref}
      className={cn(
        "w-full flex justify-center items-center",
        "py-8 px-5 sm:py-11 sm:px-8"
      )}
    >
      <div
        className={cn(
          "flex flex-col items-center justify-center text-center xs:px-3 sm:px-9 py-9 rounded-2xl",
          "xs:gap-4 xs:max-w-full",
          "sm:gap-5 ",
          "md:gap-6 ",
          "lg:gap-7 ",
          "sm:w-[1008px]",
          "md:w-[1070px]",
          "lg:w-[1320px]",
          "xl:w-[1594px]"
        )}
        style={{ backgroundColor: background_color }}
      >
        {/* Title */}
        {title && (
          <p
            className={cn(
              "xs:text-heading-6-semibold",
              "sm:text-heading-5-semibold",
              "md:text-heading-4-semibold",
              "lg:text-new-heading-34-semibold",
              "xl:text-new-heading-42-semibold",
              "line-clamp-1"
            )}
            style={{ color: title_text_color }}
          >
            {title}
          </p>
        )}
        {sub_title && sub_title.trim() !== "" && (
          <p
            className={cn(
              "text-body-3-regular",
              "sm:text-body-2-regular",
              "md:text-body-2-regular",
              "xl:text-new-body-24-regular",
              "line-clamp-4 sm:line-clamp-2"
            )}
            style={{ color: sub_title_text_color }}
          >
            {sub_title}
          </p>
        )}

        {cta_text && (
          <Button
            classType="ghost"
            onClick={handleCtaClick}
            className={cn(
              "flex items-center justify-center gap-2 mt-2",
              "xs:text-heading-9-semibold xs:px-4 xs:py-2",
              "sm:text-heading-8-semibold sm:px-5 sm:py-[10px]",
              "md:text-body-1-semibold md:px-6 md:py-3",
              "lg:text-new-body-24-semibold lg:px-7 lg:py-[14px]",
              "rounded-lg transition-all duration-200 hover:opacity-90"
            )}
            style={{
              backgroundColor: cta_button_background_color,
              color: cta_text_color,
            }}
          >
            <span>{cta_text}</span>

            {cta_button_icon && (
              <Image
                src={cta_button_icon}
                alt={cta_button_icon_alt}
                width={24}
                height={24}
                className={cn(
                  "xs:w-5 xs:h-5",
                  "sm:w-5 sm:h-5",
                  "md:w-6 md:h-6",
                  "lg:w-6 lg:h-6"
                )}
              />
            )}
          </Button>
        )}
      </div>
    </section>
  );
};

export default GetFreeCounselling1;
